
import React, { useState, useEffect } from 'react';
import { X, FileText, Send, Calendar, Clock, XCircle, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TimeRangeFilter } from '@/components/admin/TimeRangeFilter';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Modal para Emissão de Relatórios de IA
 * 
 * O que faz:
 * - Permite selecionar período para análise usando TimeRangeFilter
 * - Oferece opções de entrega (no app ou WhatsApp)
 * - Valida e confirma o número de telefone para envio via WhatsApp
 * - Dispara a criação do relatório através do hook useAIReport
 * - Permite cancelar relatórios em andamento
 * - Usa o telefone da clínica como padrão para WhatsApp
 * - Suporte a modo administrador mostrando clínica selecionada
 * - Interface totalmente responsiva para mobile e desktop
 * 
 * Onde é usado:
 * - Aberto a partir do botão "Gerar Relatório" no Dashboard
 * 
 * Como se conecta:
 * - Usa o hook useAIReport para gerenciar a lógica de criação e cancelamento
 * - Reutiliza o TimeRangeFilter do painel administrativo
 * - Busca dados da clínica para obter o telefone padrão
 */

interface AIReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateReport: (data: {
    period_start: Date;
    period_end: Date;
    delivery_method: 'in_app' | 'whatsapp';
    recipient_phone_number?: string;
    lead_filter?: 'all' | 'ads' | 'links';
  }) => void;
  onCancelReport?: (reportId: string) => void;
  isCreating: boolean;
  isCancelling?: boolean;
  currentProcessingReport?: {
    id: string;
    status: string;
  } | null;
  adminMode?: boolean;
  selectedClinica?: any;
  monthlyReportLimit?: number; // Nova prop
  reportsThisMonth?: number; // Nova prop
  isNewFlow?: boolean; // Se true usa o novo Edge Function com filtros
}

export const AIReportModal: React.FC<AIReportModalProps> = ({
  isOpen,
  onClose,
  onCreateReport,
  onCancelReport,
  isCreating,
  isCancelling = false,
  currentProcessingReport = null,
  adminMode = false,
  selectedClinica = null,
  monthlyReportLimit = 1,
  reportsThisMonth = 0,
  isNewFlow = false
}) => {
  const { clinicaId } = useClinica();
  const isMobile = useIsMobile();

  // Estados locais do modal
  const [deliveryMethod, setDeliveryMethod] = useState<'in_app' | 'whatsapp'>('in_app');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [leadFilter, setLeadFilter] = useState<'all' | 'ads' | 'links'>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<{
    start: Date | null;
    end: Date | null;
    filterName: string;
  }>({
    start: null,
    end: null,
    filterName: 'Últimos 30 dias'
  });

  // Determinar qual clinicaId usar para buscar dados
  const targetClinicaId = adminMode && selectedClinica ? selectedClinica.id : clinicaId;

  // Buscar dados da clínica para obter telefone padrão
  const { data: clinica } = useQuery({
    queryKey: ['clinica-telefone', targetClinicaId],
    queryFn: async () => {
      if (!targetClinicaId) return null;

      const { data, error } = await supabase
        .from('clinicas')
        .select('telefone, nome')
        .eq('id', targetClinicaId)
        .single();

      if (error) {
        console.error('Erro ao buscar dados da clínica:', error);
        return null;
      }

      return data;
    },
    enabled: !!targetClinicaId && isOpen
  });

  // Atualizar telefone quando os dados da clínica carregarem
  useEffect(() => {
    if (clinica?.telefone && !phoneNumber) {
      setPhoneNumber(clinica.telefone);
    }
  }, [clinica, phoneNumber]);

  // Handler para mudança de período
  const handlePeriodChange = (start: Date | null, end: Date | null, filterName: string) => {
    setSelectedPeriod({ start, end, filterName });
  };

  // Handler para criar relatório
  const handleCreateReport = () => {
    if (!selectedPeriod.start || !selectedPeriod.end) {
      return;
    }

    // Para WhatsApp, usar o número informado ou o da clínica como fallback
    const recipientPhone = deliveryMethod === 'whatsapp'
      ? (phoneNumber.trim() || clinica?.telefone || undefined)
      : undefined;

    const reportData = {
      period_start: selectedPeriod.start,
      period_end: selectedPeriod.end,
      delivery_method: deliveryMethod,
      recipient_phone_number: recipientPhone,
      lead_filter: isNewFlow ? leadFilter : undefined
    };

    console.log('📊 Criando relatório com dados:', reportData);
    onCreateReport(reportData);
  };

  // Handler para cancelar relatório
  const handleCancelReport = () => {
    if (currentProcessingReport && onCancelReport) {
      console.log('🚫 Cancelando relatório:', currentProcessingReport.id);
      onCancelReport(currentProcessingReport.id);
    }
  };

  // Validar se pode criar o relatório
  const canCreateReport = selectedPeriod.start && selectedPeriod.end &&
    (deliveryMethod === 'in_app' || (deliveryMethod === 'whatsapp' && (phoneNumber.trim().length > 0 || clinica?.telefone)));

  // Verificar se há relatório em processamento
  const hasProcessingReport = currentProcessingReport && (isCreating || currentProcessingReport.status === 'processing');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isMobile ? 'max-w-[95vw] max-h-[95vh] p-4' : 'max-w-2xl max-h-[90vh]'} overflow-y-auto`}>
        <DialogHeader className={isMobile ? 'space-y-2' : ''}>
          <DialogTitle className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-2'}`}>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className={isMobile ? 'text-base' : 'text-lg'}>Emitir Relatório de Análise</span>
            </div>
            {adminMode && selectedClinica && (
              <div className={`flex items-center gap-2 ${isMobile ? '' : 'ml-auto'}`}>
                <Building2 className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">{selectedClinica.nome}</span>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Alerta de clínica selecionada para admin */}
          {adminMode && selectedClinica && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 text-sm bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
              <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
              <span>Gerando relatório para: <strong>{selectedClinica.nome}</strong> ({selectedClinica.email})</span>
            </div>
          )}

          {/* Alerta de relatório em processamento */}
          {hasProcessingReport && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 shrink-0"></div>
                <div>
                  <h4 className="font-medium text-amber-900 text-sm">Relatório em Processamento</h4>
                  <p className="text-xs text-amber-700">
                    O processamento é automático. Cancele apenas se desejar fazer uma nova requisição.
                  </p>
                </div>
              </div>
              {onCancelReport && (
                <Button
                  onClick={handleCancelReport}
                  disabled={isCancelling}
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100 h-8 text-xs shrink-0"
                >
                  {isCancelling ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-600 mr-2"></div>
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3 h-3 mr-1.5" />
                      Cancelar
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          <div className={isNewFlow ? "grid grid-cols-1 md:grid-cols-2 gap-4" : ""}>
            {/* Seleção de Período */}
            <Card className="shadow-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <Label className="font-medium text-sm">Período da Análise</Label>
                </div>

                <TimeRangeFilter
                  onFilterChange={handlePeriodChange}
                  currentFilter={selectedPeriod.filterName}
                />

                {selectedPeriod.start && selectedPeriod.end && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span className={isMobile ? 'text-xs' : 'text-sm'}>
                      Analisando dados de {selectedPeriod.start.toLocaleDateString('pt-BR')} até {selectedPeriod.end.toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filtro de Leads (V2) */}
            {isNewFlow && (
              <Card className="shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <Label className="font-medium text-sm">O que analisar?</Label>
                  </div>

                  <RadioGroup
                    value={leadFilter}
                    onValueChange={(value: 'all' | 'ads' | 'links') => setLeadFilter(value)}
                    className="flex flex-col space-y-1.5 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="filter_all" />
                      <Label htmlFor="filter_all" className="cursor-pointer text-sm">Todos os contatos</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="ads" id="filter_ads" />
                      <Label htmlFor="filter_ads" className="cursor-pointer text-sm">Apenas anúncios (Vendas)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="links" id="filter_links" />
                      <Label htmlFor="filter_links" className="cursor-pointer text-sm">Apenas Links Pessoais</Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Método de Entrega */}
          <Card className="shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-gray-600" />
                <Label className="font-medium text-sm">Formato de entrega</Label>
              </div>

              <RadioGroup
                value={deliveryMethod}
                onValueChange={(value: 'in_app' | 'whatsapp') => setDeliveryMethod(value)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <div className="flex items-start space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="in_app" id="in_app" className="mt-1" />
                  <Label htmlFor="in_app" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm">No Dashboard</div>
                      <Badge variant="secondary" className="text-[10px] px-1 h-4">Recomendado</Badge>
                    </div>
                    <div className="text-gray-500 text-xs mt-0.5">
                      Visualização imediata no sistema
                    </div>
                  </Label>
                </div>

                <div className="flex items-start space-x-2 p-3 border rounded-lg hover:bg-gray-50">
                  <RadioGroupItem value="whatsapp" id="whatsapp" className="mt-1" />
                  <Label htmlFor="whatsapp" className="flex-1 cursor-pointer">
                    <div className="font-medium text-sm">Via WhatsApp</div>
                    <div className="text-gray-500 text-xs mt-0.5">
                      Receba direto no seu celular
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Campo de telefone quando WhatsApp selecionado */}
              {deliveryMethod === 'whatsapp' && (
                <div className="space-y-1.5 pt-2 border-t">
                  <Label htmlFor="phone" className="text-sm">Número de Envio</Label>
                  <Input
                    id="phone"
                    placeholder="(11) 99999-9999"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="max-w-xs h-9 text-sm"
                  />
                  <p className="text-gray-500 text-xs">
                    {clinica?.telefone ?
                      `Telefone default da clínica: ${clinica.telefone}` :
                      'Verifique o DDI e o 9 extra.'
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informações sobre o relatório */}
          <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100">
            <Clock className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <p>
              A inteligência artificial processará esses dados e gerará uma análise de performance com duração estimada de <strong>2 a 5 minutos</strong>.
            </p>
          </div>
        </div>

        {/* Botões de ação */}
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end gap-3'} pt-4`}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCreating || isCancelling}
            className={isMobile ? 'w-full' : ''}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateReport}
            disabled={!canCreateReport || isCreating || hasProcessingReport}
            className={`bg-blue-600 hover:bg-blue-700 ${isMobile ? 'w-full' : ''}`}
          >
            {isCreating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processando...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Gerar Relatório
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
